// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { navigationDrawerStyles } from "@conklin/Overrides/Components/NavigationDrawer";
import { HEADER_HEIGHT_CSS_VAR } from "@conklin/XDL/constants";
import IconHeaderCaretRight from "@conklin/XDL/Icons/IconHeaderCaretRight";
import IconSignout from "@conklin/XDL/Icons/IconSignout";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, createLinkIconStyles, xScreen } from "@conklin/XDL/Utilities";
import { ButtonIcon, ButtonWrapper } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

navigationDrawerStyles.menuTriggerButton = {
    size: 16,
    variant: "secondary",
    css: css`
        &,
        &:hover,
        &:focus {
            padding: 0;
            border: none;
            background-color: transparent;
        }

        ${ButtonIcon} {
            color: ${color("common.ui4")};
        }
    `,
};

navigationDrawerStyles.drawer = {
    size: 360,
    cssOverrides: {
        drawerBody: css`
            top: var(${HEADER_HEIGHT_CSS_VAR});
            transform: translate(-100%, 0);
            height: calc(100vh - var(${HEADER_HEIGHT_CSS_VAR}));
            box-shadow: none;
            padding: 10px 0 16px 0;
            background-color: ${color("common.ui1")};
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
        drawerTitle: css`
            display: none;
        `,
        scrim: css`
            background-color: ${color("common.black", 0.5)};
            top: var(${HEADER_HEIGHT_CSS_VAR});
            height: calc(100vh - var(${HEADER_HEIGHT_CSS_VAR}));
        `,
    },
};

navigationDrawerStyles.drawerSectionWrapper = {
    css: css``,
};

navigationDrawerStyles.mainNavigationRow = {
    color: "common.ui1",
    css: css`
        margin: 0;
        padding: 8px 16px;
        &:hover,
        &:focus {
            background-color: ${color("common.ui11")};
            color: ${color("common.white")};
            ${IconWrapper}, ${TypographyStyle} {
                color: ${color("common.white")};
            }
        }
    `,
    moreIconProps: {
        src: IconHeaderCaretRight,
        size: 14,
        css: css`
            color: ${color("common.ui4")};
            height: 20px;
            &,
            svg {
                width: auto;
            }
            justify-content: flex-end;
        `,
    },
};

navigationDrawerStyles.mainNavigationRowTypography = {
    color: "text.main",
    weight: fontConfig.weight.regular,
    size: 16,
    lineHeight: "19px",
};

navigationDrawerStyles.drawerSectionSeparator = {
    css: css`
        margin: 16px auto;
        background-color: ${color("common.border")};
        height: 1px;
        width: 100%;
    `,
};

navigationDrawerStyles.accountPanelSectionWrapper = {
    css: css`
        display: flex;
        align-items: center;
        width: 100%;
    `,
};

navigationDrawerStyles.signOutButton = {
    ...createLinkIconStyles(IconSignout),
};

navigationDrawerStyles.panelMenu = {
    bodyColor: "common.ui1",
    headerColor: "text",
    backButton: {
        css: css`
            &,
            &:not(:disabled):hover,
            &:focus {
                padding: 0;
                border: none;
                background-color: transparent;
                font-size: 16px;
                line-height: 19px;
                min-height: 22px;
                color: ${color("text")};
            }

            ${IconWrapper} {
                color: ${color("common.ui4")};
                margin-right: 8px;
            }
        `,
    },
    panelChildrenSeparator: {
        css: css`
            margin: 0 auto 8px auto;
            background-color: ${color("common.border")};
            height: 1px;
            width: calc(100% - 32px);
        `,
    },
    brandLink: {
        typographyProps: {
            size: 16,
            lineHeight: "19px",
            weight: fontConfig.weight.regular,
        },
        css: css`
            &&&:hover,
            &&&:focus {
                ${TypographyStyle} {
                    color: ${color("common.white")};
                }
            }
            padding: 8px 16px;
            width: 100%;
            &&& ${TypographyStyle} {
                color: ${color("text")};
            }
        `,
    },
    cssOverrides: {
        menu: css`
            padding: 0 0 16px 0;
        `,
        children: css`
            background-color: ${color("common.ui1")};
            > .previous {
                padding: 8px 0;
                ${ButtonWrapper} {
                    justify-content: flex-start;
                    width: 100%;
                    padding: 8px 16px;
                    &:hover,
                    &:focus {
                        background-color: ${color("common.ui11")};
                        color: ${color("common.ui1")};
                        ${IconWrapper} {
                            color: ${color("common.ui1")};
                        }
                    }
                }
            }
            > .brand-link {
                padding: 0 0 8px;
            }
        `,
        wrapper: css`
            ${StyledButton} {
                padding: 8px 16px;
                &:hover,
                &:focus {
                    background-color: ${color("common.ui11")};
                    ${IconWrapper}, ${TypographyStyle} {
                        color: ${color("common.ui1")};
                    }
                }
            }
        `,
    },
};
